// storm-allow-pin-palette
// PIN-DATA: this file wires the MapLibre symbol layer + sprite atlas which
// reads the D-69 LOCKED status palette. The `// storm-allow-pin-palette`
// directive above silences the chrome ESLint rule
// (storm/no-legacy-palette-in-chrome) for this file. Locked-grep gate honored
// via `LOCKED:OVERRIDE:` (start-of-line + colon-delimited; matches the
// gate's regex `^LOCKED:OVERRIDE:` at chrome-pin-locked-grep.sh:31) on
// the commit that introduced this comment (Phase 9 Wave 2 pre-swap).
/**
 * MapLibre style helpers for the Plan 03-04 web map.
 *
 * The plan considered building a self-contained style.json from scratch but
 * the OpenFreeMap "liberty" style has dozens of layers (water/roads/labels/
 * place names/etc.). The pragmatic shape is:
 *   1. Mount `maplibregl.Map` with style URL = OFM liberty.
 *   2. After map.load, programmatically add:
 *        - the PMTiles fallback source + a minimal background layer
 *          (visibility=none until the tile-fallback flip kicks in);
 *        - the `assets` GeoJSON source + symbol layer + label layer.
 *
 * `ensurePMTilesProtocol` registers the `pmtiles://` URL handler globally
 * (idempotent — MapView re-mounts during HMR must not double-register).
 *
 * The OFM tile attribution is rendered by MapLibre's built-in
 * AttributionControl with `compact: false` (set on Map construction in
 * MapView.tsx). MapLibre flips it automatically in RTL via the `dir`
 * inheritance from `<html dir="rtl">`. Phase 3 must NOT override this.
 */
import maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";

let _pmtilesRegistered = false;

/** Idempotent: register `pmtiles://...` protocol once per page lifecycle.
 *
 * pmtiles 4.x exposes both `.tile` (V3-or-V4 polymorphic) and `.tilev4`
 * (strict MapLibre v4+ AbortController-based async). MapLibre 5 expects the
 * AbortController-based shape, so we register `.tilev4` directly to avoid
 * the polymorphic type union — same runtime behavior, cleaner types.
 */
export function ensurePMTilesProtocol(): void {
  if (_pmtilesRegistered) return;
  const protocol = new Protocol();
  maplibregl.addProtocol("pmtiles", protocol.tilev4);
  _pmtilesRegistered = true;
}

export const SPRITE_PATH = "/sprites/storm";

/** Default OpenFreeMap style URL — the primary tile source per D-82. */
export const OFM_STYLE_URL = "https://tiles.openfreemap.org/styles/liberty";

/**
 * Build the PMTiles source descriptor that's added to the live map after
 * `map.on('load')`. We declare it as a vector source with a `pmtiles://`
 * URL; the registered protocol handler resolves byte-range requests against
 * the Caddy `/tiles/storm.pmtiles` route (D-81).
 *
 * NOTE on layer parity: a complete fallback would mirror every OFM-derived
 * layer with its PMTiles equivalent. Phase 3 dev ships ONLY a single
 * background-fill layer for the fallback, since the PMTiles archive built
 * by `ops/scripts/build-pmtiles.sh` is operator-managed and may not yet be
 * deployed in dev. Production deploys (Phase 7 release prep) replace the
 * placeholder with a richer fallback style; the asset symbol layer remains
 * unchanged across both modes since it reads from the GeoJSON source, not
 * from any tile source.
 */
export function pmtilesUrl(): string {
  // Allow override at build time (Vite env) for staging. Default = same-origin
  // /tiles/storm.pmtiles per D-81 Caddy route.
  const fromEnv = (import.meta.env.VITE_PMTILES_URL as string | undefined) ?? "";
  if (fromEnv) return fromEnv;
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/tiles/storm.pmtiles`;
  }
  return "/tiles/storm.pmtiles";
}
